import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import Home from './sections/Home'
import Header from './sections/Header'
import Sidebar from './sections/Sidebar'
import LoadingScreen from './components/LoadingScreen'
import { ModalProvider, useModal } from './context/ModalContext'
import { LanguageProvider } from './context/LanguageContext'
import { ThemeProvider } from './context/ThemeContext'
import './styles/Scrollbar.css'

const About = lazy(() => import('./sections/About'))
const Work = lazy(() => import('./sections/Work'))
const Footer = lazy(() => import('./sections/Footer'))

const AppContent = ({ onGrainientReady, started }) => {
	const { isProjectModalOpen, isContactModalOpen } = useModal()

	useEffect(() => {
		document.documentElement.style.overflow =
			isProjectModalOpen || isContactModalOpen ? 'hidden' : ''
	}, [isProjectModalOpen, isContactModalOpen])

	return (
		<div className='scroll-smooth min-h-screen bg-[var(--bg-primary)] overflow-x-hidden transition-colors duration-300'>
			<div className={isProjectModalOpen ? 'hidden' : ''}>
				<Sidebar started={started} />
				<Header />
			</div>
			<Home onGrainientReady={onGrainientReady} started={started} />
			<Suspense>
				<About />
				<Work />
				<Footer />
			</Suspense>
		</div>
	)
}

function App() {
	const [grainientReady, setGrainientReady] = useState(false)
	const [started, setStarted] = useState(false)
	const initialHashRef = useRef(window.location.hash.replace('#', ''))
	const handleGrainientReady = useCallback(() => {
		setGrainientReady(true)
	}, [])

	useEffect(() => {
		if (!started) {
			document.documentElement.style.overflow = 'hidden'
		} else {
			document.documentElement.style.overflow = ''
		}
	}, [started])

	useEffect(() => {
		if (!started) return
		const hash = initialHashRef.current
		if (!hash) return
		const el = document.getElementById(hash)
		if (!el) return
		const timer = window.setTimeout(() => {
			el.scrollIntoView({ behavior: 'smooth' })
		}, 300)
		return () => window.clearTimeout(timer)
	}, [started])

	useEffect(() => {
		const handlePopState = () => {
			const hash = window.location.hash.replace('#', '')
			if (!hash) return
			document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' })
		}
		window.addEventListener('popstate', handlePopState)
		return () => window.removeEventListener('popstate', handlePopState)
	}, [])

	return (
		<ThemeProvider>
			<LanguageProvider>
				<ModalProvider>
					<LoadingScreen grainientReady={grainientReady} onExit={() => setStarted(true)} />
					<AppContent onGrainientReady={handleGrainientReady} started={started} />
				</ModalProvider>
			</LanguageProvider>
		</ThemeProvider>
	)
}

export default App
